/**
 * @file api-content.ts
 * @description Клиент `/admin/content/*` для @chaiberi/admin (phase12.0).
 *
 * Зеркало контроллеров apps/api/src/content/*. Все методы возвращают `data`
 * напрямую (apiFetch разворачивает `{data}/{error}`).
 */

import { apiFetch } from "./api-client";
import type { RedirectOnSlugInput, RedirectOnSlugResult } from "./api-catalog";
import type { SeoKeywordsDto, SeoKeywordsInput } from "./site-seo/keywords";

// ─── Authors ─────────────────────────────────────────────────────────────

export interface ContentAuthor {
  id: string;
  slug: string;
  name: string;
  bio: string;
  photoMediaId: string | null;
  telegramHandle: string | null;
  vkHandle: string | null;
  instagramHandle: string | null;
  userId: string | null;
  articlesCount?: number;
}

export interface CreateAuthorInput {
  name: string;
  bio: string;
  slug?: string;
  photoMediaId?: string;
  telegramHandle?: string;
  vkHandle?: string;
  instagramHandle?: string;
  userId?: string;
}

/**
 * Правка автора: `null` очищает фото и соцсети (серверный UpdateAuthorDto
 * принимает `string | null`; phase19.16 Р1 — карточка автора).
 */
export type UpdateAuthorInput = Partial<Omit<CreateAuthorInput, "photoMediaId" | "telegramHandle" | "vkHandle">> & {
  photoMediaId?: string | null;
  telegramHandle?: string | null;
  vkHandle?: string | null;
};

export const contentAuthors = {
  list: () => apiFetch<ContentAuthor[]>("/admin/content/authors"),
  findOne: (id: string) => apiFetch<ContentAuthor>(`/admin/content/authors/${id}`),
  create: (input: CreateAuthorInput) =>
    apiFetch<ContentAuthor>("/admin/content/authors", { method: "POST", json: input }),
  update: (id: string, input: UpdateAuthorInput) =>
    apiFetch<ContentAuthor>(`/admin/content/authors/${id}`, { method: "PATCH", json: input }),
  remove: (id: string) =>
    apiFetch<void>(`/admin/content/authors/${id}`, { method: "DELETE" }),
};

// ─── Categories ──────────────────────────────────────────────────────────

export interface ContentCategory extends RedirectOnSlugResult {
  id: string;
  slug: string;
  name: string;
  description: string;
  seoTitle: string | null;
  seoDescription: string | null;
  sortOrder: number;
  articlesCount?: number;
}

export interface CreateContentCategoryInput {
  name: string;
  description: string;
  slug?: string;
  seoTitle?: string;
  seoDescription?: string;
  sortOrder?: number;
}

/** Р3в (SB131): `redirectFromOldSlug` — переадресовать со старого адреса рубрики. */
export type UpdateContentCategoryInput = Partial<CreateContentCategoryInput> & RedirectOnSlugInput;

export const contentCategories = {
  list: () => apiFetch<ContentCategory[]>("/admin/content/categories"),
  findOne: (id: string) => apiFetch<ContentCategory>(`/admin/content/categories/${id}`),
  create: (input: CreateContentCategoryInput) =>
    apiFetch<ContentCategory>("/admin/content/categories", { method: "POST", json: input }),
  update: (id: string, input: UpdateContentCategoryInput) =>
    apiFetch<ContentCategory>(`/admin/content/categories/${id}`, { method: "PATCH", json: input }),
  remove: (id: string) =>
    apiFetch<void>(`/admin/content/categories/${id}`, { method: "DELETE" }),
};

// ─── Tags ─────────────────────────────────────────────────────────────────

export interface ContentTag {
  id: string;
  slug: string;
  name: string;
  articlesCount?: number;
}

export interface CreateContentTagInput {
  name: string;
  slug?: string;
}

export type UpdateContentTagInput = Partial<CreateContentTagInput>;

export const contentTags = {
  list: () => apiFetch<ContentTag[]>("/admin/content/tags"),
  findOne: (id: string) => apiFetch<ContentTag>(`/admin/content/tags/${id}`),
  create: (input: CreateContentTagInput) =>
    apiFetch<ContentTag>("/admin/content/tags", { method: "POST", json: input }),
  update: (id: string, input: UpdateContentTagInput) =>
    apiFetch<ContentTag>(`/admin/content/tags/${id}`, { method: "PATCH", json: input }),
  remove: (id: string) =>
    apiFetch<void>(`/admin/content/tags/${id}`, { method: "DELETE" }),
};

// ─── Articles ────────────────────────────────────────────────────────────

export type ContentArticleStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export interface ArticleListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  status: ContentArticleStatus;
  publishedAt: string | null;
  updatedAt: string;
  authorId: string;
  authorName: string;
  categoryId: string;
  categoryName: string;
  categorySlug: string;
  coverMediaId: string | null;
  coverAlt: string;
  tagSlugs: string[];
  readingTimeMin: number | null;
  noIndex: boolean;
}

export interface ArticleDetail extends ArticleListItem, SeoKeywordsDto, RedirectOnSlugResult {
  bodyJson: unknown;
  bodyHtml: string;
  seoTitle: string | null;
  seoDescription: string | null;
  canonicalUrl: string | null;
  ogImageMediaId: string | null;
}

export interface CreateArticleInput extends SeoKeywordsInput, RedirectOnSlugInput {
  title: string;
  excerpt: string;
  bodyJson: Record<string, unknown>;
  bodyHtml: string;
  authorId: string;
  categoryId: string;
  tagIds: string[];
  coverAlt: string;
  coverMediaId?: string;
  slug?: string;
  seoTitle?: string;
  seoDescription?: string;
  canonicalUrl?: string;
  ogImageMediaId?: string;
  noIndex?: boolean;
}

export type UpdateArticleInput = Partial<CreateArticleInput>;

export interface ArticleListFilters {
  status?: ContentArticleStatus;
  categoryId?: string;
  authorId?: string;
  tagId?: string;
  search?: string;
}

function articlesQuery(f?: ArticleListFilters): string {
  if (!f) return "";
  const params = new URLSearchParams();
  if (f.status) params.set("status", f.status);
  if (f.categoryId) params.set("categoryId", f.categoryId);
  if (f.authorId) params.set("authorId", f.authorId);
  if (f.tagId) params.set("tagId", f.tagId);
  if (f.search) params.set("search", f.search);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export const contentArticles = {
  list: (f?: ArticleListFilters) =>
    apiFetch<ArticleListItem[]>(`/admin/content/articles${articlesQuery(f)}`),
  findOne: (id: string) => apiFetch<ArticleDetail>(`/admin/content/articles/${id}`),
  create: (input: CreateArticleInput) =>
    apiFetch<ArticleDetail>("/admin/content/articles", { method: "POST", json: input }),
  update: (id: string, input: UpdateArticleInput) =>
    apiFetch<ArticleDetail>(`/admin/content/articles/${id}`, { method: "PATCH", json: input }),
  publish: (id: string) =>
    apiFetch<ArticleDetail>(`/admin/content/articles/${id}/publish`, { method: "POST" }),
  unpublish: (id: string) =>
    apiFetch<ArticleDetail>(`/admin/content/articles/${id}/unpublish`, { method: "POST" }),
  archive: (id: string) =>
    apiFetch<ArticleDetail>(`/admin/content/articles/${id}/archive`, { method: "POST" }),
  remove: (id: string) =>
    apiFetch<void>(`/admin/content/articles/${id}`, { method: "DELETE" }),
};
